import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import * as _ from "lodash";

import {
  ActivityButton,
  AppRichEditor,
  SequenceNumberInput,
} from "../../../../components/Common";

import { ErrorHandlingTypeEnum, SequenceEnum } from "../../../../enums";
import { CreateAssignmentQuestionRequest } from "../../../../interfaces/lecture/assignment";
import {
  useGetAssignmentQuestionQuery,
  useAddAssignmentQuestionMutation,
  useUpdateAssignmentQuestionMutation,
} from "../../../../store/api/assignmentQuestionApi";
import { ParsedError } from "../../../../interfaces";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  assignmentId: string;
};
export const AddEditAssignmentQuestionModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  assignmentId,
}) => {
  const { t } = useTranslation();
  const formRef: any = useRef();

  const [
    addAssignmentQuestion,
    {
      isLoading: isLoadingAdd,
      error: errorAdd,
      isError: isErrorAdd,
      isSuccess: isSuccessAdd,
    },
  ] = useAddAssignmentQuestionMutation();
  const [
    updateAssignmentQuestion,
    {
      isLoading: isLoadingUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
      isSuccess: isSuccessUpdate,
    },
  ] = useUpdateAssignmentQuestionMutation();

  const {
    data: dataQuestion,
    isLoading: isLoadingQuestion,
    isError: isErrorQuestion,
  } = useGetAssignmentQuestionQuery(id as string, { skip: !id });

  //#region form
  const validationSchema = Yup.object<CreateAssignmentQuestionRequest>().shape({
    title: Yup.object({
      en: Yup.string()
        .max(
          100,
          t("maxLength", { maxLength: 100, displayName: "englishValue" }),
        )
        .required(
          t("isNotEmpty", {
            displayName: "englishValue",
          }),
        ),
      ar: Yup.string()
        .max(
          100,
          t("maxLength", { maxLength: 100, displayName: "englishValue" }),
        )
        .required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          }),
        ),
    }),
    content: Yup.string().required(
      t("isNotEmpty", {
        displayName: "content",
      }),
    ),
    answerContent: Yup.string().required(
      t("isNotEmpty", {
        displayName: "answerContent",
      }),
    ),
    sequenceNumber: Yup.number().required(
      t("isNotEmpty", {
        displayName: "sequenceNumber",
      }),
    ),
  });

  //#endregion

  const isEdit = Boolean(id);
  const initialValues: CreateAssignmentQuestionRequest =
    isEdit && dataQuestion
      ? {
          title: dataQuestion.title,
          content: dataQuestion.content ?? [],
          answerContent: dataQuestion.answerContent ?? [],
          sequenceNumber: dataQuestion.sequenceNumber ?? 0,
        }
      : {
          title: { en: "", ar: "" },
          content: [],
          answerContent: [],
          sequenceNumber: 0,
        };

  //#region handlers
  const onClose = () => {
    formRef.current?.resetForm();
    onCloseClick(!show);
  };

  const handleFormSubmit = async (values: any) => {
    if (id) {
      await updateAssignmentQuestion({
        id,
        data: { ...values },
      }).unwrap();
    } else {
      await addAssignmentQuestion({
        assignmentId,
        data: { ...values },
      }).unwrap();
    }
  };

  //#endregion

  //#region effects
  useEffect(() => {
    if (id && dataQuestion && formRef.current?.setValues) {
      formRef.current.setValues({
        title: dataQuestion.title,
        sequenceNumber: dataQuestion.sequenceNumber ?? 0,
        answerContent: dataQuestion.answerContent,
        content: dataQuestion.content,
      });
    }
  }, [dataQuestion, isLoadingQuestion, id]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (
      _error?.type === ErrorHandlingTypeEnum.FORM &&
      formRef.current?.setErrors
    ) {
      formRef.current.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  useEffect(() => {
    if (
      (!isLoadingAdd && isSuccessAdd) ||
      (!isLoadingUpdate && isSuccessUpdate)
    ) {
      onClose();
    }
  }, [isLoadingAdd, isSuccessAdd, isLoadingUpdate, isSuccessUpdate]);

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-xl"
      >
        <ModalBody>
          <Formik
            key={id ?? "add"}
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              setFieldValue,
              touched,
              errors,
              setFieldTouched,
              handleBlur,
              handleChange,
            }) => (
              <Form id="form1">
                <SequenceNumberInput
                  tabIndex={1}
                  value={values.sequenceNumber}
                  onChange={(e) => {
                    e.preventDefault();
                    const { value } = e.target;
                    const regex =
                      /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                    if (regex.test(value.toString())) {
                      setFieldValue("sequenceNumber", value);
                    } else {
                      setFieldValue("sequenceNumber", "");
                    }
                  }}
                  onBlur={handleBlur}
                  invalid={
                    touched.sequenceNumber && errors.sequenceNumber
                      ? true
                      : false
                  }
                  sequenceType={SequenceEnum.LECTURE_ASSIGNMENT}
                  onGenerateSequenceNumber={(v: number) => {
                    setFieldValue("sequenceNumber", v);
                  }}
                  mode={id ? "edit" : "add"}
                  request={{
                    type: SequenceEnum.ASSIGNMENT_QUESTION,
                    assignmentId: assignmentId,
                  }}
                />
                {touched.sequenceNumber && errors.sequenceNumber ? (
                  <FormFeedback type="invalid">
                    {errors.sequenceNumber as string}
                  </FormFeedback>
                ) : null}
                <div className="mb-3">
                  <label htmlFor="title.en" className="form-label">
                    {t("AssignmentQuestionTopicInEnglish")}
                    <span className="text-danger">*</span>
                    <span className="text-info">
                      &nbsp;({t("AssignmentQuestionTopicHint")})
                    </span>
                  </label>
                  <Input
                    autoComplete="off"
                    id="title.en"
                    name="title.en"
                    value={values?.title.en}
                    type="text"
                    className="form-control"
                    placeholder={t("enterInEnglish")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={
                      touched.title?.en && errors.title?.en ? true : false
                    }
                  />
                  {touched.title?.en && errors.title?.en ? (
                    <FormFeedback type="invalid">
                      {errors.title?.en as string}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="mb-3">
                  <label htmlFor="title.ar" className="form-label">
                    {t("AssignmentQuestionTopicInArabic")}
                    <span className="text-danger">*</span>
                    <span className="text-info">
                      &nbsp;({t("AssignmentQuestionTopicHint")})
                    </span>
                  </label>
                  <Input
                    style={{ fontFamily: "aljannat", direction: "rtl" }}
                    autoComplete="off"
                    id="title.ar"
                    name="title.ar"
                    value={values.title?.ar}
                    type="text"
                    className="form-control"
                    placeholder={t("enterInArabic")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={
                      touched.title?.ar && errors.title?.ar ? true : false
                    }
                  />
                  {touched.title?.ar && errors.title?.ar ? (
                    <FormFeedback type="invalid">
                      {errors.title?.ar as string}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="mb-3">
                  <Label htmlFor="content" className="form-label">
                    {t("QuestionContent")}
                  </Label>
                  <AppRichEditor
                    key={id ?? "add"}
                    initialValue={
                      id && dataQuestion ? dataQuestion.content : ""
                    }
                    onChange={(data: any) => {
                      setFieldValue("content", data);
                    }}
                    onFocus={() => {
                      setFieldTouched("content", true, true);
                    }}
                  />
                  {touched.content && errors.content ? (
                    <Label className="invalid-feedback d-block">
                      {errors.content as string}
                    </Label>
                  ) : null}
                </div>
                <div className="mb-3">
                  <Label htmlFor="answer" className="form-label">
                    {t("CorrectAnswer")}
                  </Label>
                  <AppRichEditor
                    key={`answer-${id ?? "add"}`}
                    initialValue={
                      id && dataQuestion ? dataQuestion.answerContent : ""
                    }
                    onChange={(data: any) => {
                      setFieldValue("answerContent", data);
                    }}
                    onFocus={() => {
                      setFieldTouched("answerContent", true, true);
                    }}
                  />
                  {touched.answerContent && errors.answerContent ? (
                    <Label className="invalid-feedback d-block">
                      {errors.answerContent as string}
                    </Label>
                  ) : null}
                </div>
              </Form>
            )}
          </Formik>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            form="form1"
            inProgress={isLoadingAdd || isLoadingUpdate}
            disabled={isLoadingAdd || isLoadingUpdate || isErrorQuestion}
          ></ActivityButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};
